import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { apiClient } from '../api/client';
import Logo from '../components/Logo';
import { Calendar, MapPin, Store, ArrowRight, Users, Trophy } from 'lucide-react';

// Abstract map SVG of Severna Park, MD area - colorful version
const MapBackground = () => (
  <svg
    className="absolute inset-0 w-full h-full"
    viewBox="0 0 800 600"
    preserveAspectRatio="xMidYMid slice"
    xmlns="http://www.w3.org/2000/svg"
  >
    <defs>
      <pattern id="grid" width="40" height="40" patternUnits="userSpaceOnUse">
        <path d="M 40 0 L 0 0 0 40" fill="none" stroke="#e0e7ff" strokeWidth="0.5" />
      </pattern>
      <linearGradient id="waterGradient" x1="0%" y1="0%" x2="100%" y2="0%">
        <stop offset="0%" stopColor="#38bdf8" />
        <stop offset="50%" stopColor="#0ea5e9" />
        <stop offset="100%" stopColor="#06b6d4" />
      </linearGradient>
      <linearGradient id="parkGradient" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#4ade80" />
        <stop offset="100%" stopColor="#22c55e" />
      </linearGradient>
    </defs>

    {/* Base with subtle warm tint */}
    <rect width="100%" height="100%" fill="#fefce8" />
    <rect width="100%" height="100%" fill="url(#grid)" />

    {/* Severn River - vibrant blue flowing through */}
    <path
      d="M 0 200 Q 150 180 250 220 Q 400 280 500 240 Q 650 180 800 200"
      fill="none"
      stroke="url(#waterGradient)"
      strokeWidth="70"
      opacity="0.5"
    />
    <path
      d="M 0 200 Q 150 180 250 220 Q 400 280 500 240 Q 650 180 800 200"
      fill="none"
      stroke="#38bdf8"
      strokeWidth="35"
      opacity="0.6"
    />

    {/* Magothy River area - top right */}
    <path
      d="M 600 0 Q 650 80 700 60 Q 780 40 800 80"
      fill="none"
      stroke="#22d3ee"
      strokeWidth="45"
      opacity="0.5"
    />

    {/* Major roads - warm terracotta colors */}
    <path d="M 100 0 L 100 600" fill="none" stroke="#fb923c" strokeWidth="4" opacity="0.5" />
    <path d="M 300 0 Q 320 300 280 600" fill="none" stroke="#f97316" strokeWidth="5" opacity="0.6" />
    <path d="M 500 0 L 520 600" fill="none" stroke="#fb923c" strokeWidth="4" opacity="0.5" />
    <path d="M 0 400 Q 400 380 800 420" fill="none" stroke="#f97316" strokeWidth="5" opacity="0.6" />
    <path d="M 0 100 Q 200 120 400 90 Q 600 60 800 100" fill="none" stroke="#fdba74" strokeWidth="4" opacity="0.5" />

    {/* B&A Trail - bright green dashed */}
    <path
      d="M 50 600 Q 100 500 150 400 Q 200 300 180 200 Q 160 100 200 0"
      fill="none"
      stroke="#22c55e"
      strokeWidth="8"
      strokeDasharray="15 8"
      opacity="0.7"
    />

    {/* Neighborhood clusters - fun colorful bubbles */}
    <circle cx="350" cy="350" r="85" fill="#a78bfa" opacity="0.3" />
    <circle cx="350" cy="350" r="50" fill="#c4b5fd" opacity="0.4" />

    <circle cx="450" cy="450" r="65" fill="#fb7185" opacity="0.3" />
    <circle cx="450" cy="450" r="35" fill="#fda4af" opacity="0.4" />

    <circle cx="250" cy="480" r="55" fill="#fbbf24" opacity="0.3" />
    <circle cx="250" cy="480" r="30" fill="#fcd34d" opacity="0.4" />

    <circle cx="550" cy="350" r="75" fill="#34d399" opacity="0.3" />
    <circle cx="550" cy="350" r="45" fill="#6ee7b7" opacity="0.4" />

    <circle cx="150" cy="350" r="50" fill="#60a5fa" opacity="0.3" />
    <circle cx="150" cy="350" r="28" fill="#93c5fd" opacity="0.4" />

    <circle cx="650" cy="450" r="45" fill="#f472b6" opacity="0.3" />
    <circle cx="650" cy="450" r="25" fill="#f9a8d4" opacity="0.4" />

    {/* Park areas - bright green spaces */}
    <ellipse cx="400" cy="320" rx="35" ry="22" fill="url(#parkGradient)" opacity="0.5" />
    <ellipse cx="280" cy="420" rx="28" ry="18" fill="#4ade80" opacity="0.5" />
    <ellipse cx="520" cy="380" rx="24" ry="28" fill="#22c55e" opacity="0.45" />
    <ellipse cx="180" cy="480" rx="20" ry="15" fill="#86efac" opacity="0.5" />
    <ellipse cx="600" cy="320" rx="22" ry="18" fill="#4ade80" opacity="0.45" />

    {/* Small connector roads - softer */}
    <path d="M 200 300 Q 250 320 300 300" fill="none" stroke="#fed7aa" strokeWidth="3" opacity="0.6" />
    <path d="M 400 350 Q 450 370 500 350" fill="none" stroke="#fed7aa" strokeWidth="3" opacity="0.6" />
    <path d="M 350 400 L 350 500" fill="none" stroke="#fde68a" strokeWidth="3" opacity="0.5" />
    <path d="M 450 380 L 480 480" fill="none" stroke="#fde68a" strokeWidth="3" opacity="0.5" />

    {/* Decorative dots for points of interest */}
    <circle cx="320" cy="370" r="8" fill="#f43f5e" opacity="0.7" />
    <circle cx="420" cy="340" r="8" fill="#8b5cf6" opacity="0.7" />
    <circle cx="500" cy="400" r="8" fill="#0ea5e9" opacity="0.7" />
    <circle cx="280" cy="450" r="8" fill="#f59e0b" opacity="0.7" />
    <circle cx="380" cy="420" r="8" fill="#10b981" opacity="0.7" />
    <circle cx="550" cy="380" r="8" fill="#ec4899" opacity="0.7" />
  </svg>
);

interface CampaignRun {
  id: string;
  campaignName: string;
  description: string | null;
  imageUrl: string | null;
  headerColor: string | null;
  startDate: string;
  endDate: string;
  status: 'ACTIVE' | 'DRAFT';
  storeCount: number;
}

interface StoreLogo {
  id: string;
  name: string;
  logoUrl: string | null;
  primaryColor: string | null;
}

interface Stats {
  customers: number;
  stores: number;
  winners: number;
}

function formatDate(dateString: string) {
  return new Date(dateString).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

// Rotating store logos component
function RotatingLogos({ stores }: { stores: StoreLogo[] }) {
  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    if (stores.length <= 1) return;
    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % stores.length);
    }, 3000);
    return () => clearInterval(interval);
  }, [stores.length]);

  if (stores.length === 0) return null;

  const store = stores[currentIndex];

  return (
    <div className="flex flex-col items-end gap-1">
      <span className="text-xs text-gray-500">Featuring:</span>
      <div className="w-28 h-28 bg-white/90 rounded-xl shadow-md flex items-center justify-center overflow-hidden transition-all duration-500 p-2">
        {store.logoUrl ? (
          <img
            src={store.logoUrl}
            alt={store.name}
            className="max-w-full max-h-full object-contain"
          />
        ) : (
          <div className="text-center">
            <Store className="w-10 h-10 text-gray-400 mx-auto" />
            <span className="text-xs text-gray-500 mt-1 block">{store.name}</span>
          </div>
        )}
      </div>
    </div>
  );
}

export default function HomePage() {
  const [campaigns, setCampaigns] = useState<CampaignRun[]>([]);
  const [stores, setStores] = useState<StoreLogo[]>([]);
  const [stats, setStats] = useState<Stats | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function fetchData() {
      try {
        const [campaignRes, storesRes, statsRes] = await Promise.all([
          apiClient.get<{ success: boolean; data: CampaignRun[] }>('/campaign'),
          apiClient.get<{ success: boolean; data: StoreLogo[] }>('/stats/stores'),
          apiClient.get<{ success: boolean; data: Stats }>('/stats'),
        ]);
        setCampaigns(campaignRes.data.data);
        setStores(storesRes.data.data);
        setStats(statsRes.data.data);
      } catch (error) {
        console.error('Failed to fetch data:', error);
      } finally {
        setLoading(false);
      }
    }
    fetchData();
  }, []);

  const now = new Date();
  // "Shop Now" = ACTIVE campaigns that have already started
  const shopNowCampaigns = campaigns.filter((c) =>
    c.status === 'ACTIVE' && new Date(c.startDate) <= now
  );
  // "Upcoming" = ACTIVE campaigns that haven't started yet, or DRAFT campaigns
  const upcomingCampaigns = campaigns.filter((c) =>
    (c.status === 'ACTIVE' && new Date(c.startDate) > now) || c.status === 'DRAFT'
  );

  return (
    <div className="min-h-screen relative overflow-hidden">
      {/* Map Background */}
      <div className="absolute inset-0">
        <MapBackground />
      </div>

      {/* Gradient overlay for better readability */}
      <div className="absolute inset-0 bg-gradient-to-br from-white/70 via-white/50 to-amber-50/30" />

      <div className="relative z-10 py-12 px-4">
        <div className="max-w-4xl mx-auto">
          {/* Header with rotating logos */}
          <div className="flex items-start justify-between mb-8">
            <div className="flex-1" />
            <div className="text-center flex-shrink-0">
              <div className="flex justify-center mb-4">
                <Logo size="xl" />
              </div>
              <h1 className="text-4xl font-bold text-gray-900 mb-2">Discover The Park</h1>
              <p className="text-xl text-gray-600">Explore local businesses in Severna Park</p>
            </div>
            <div className="flex-1 flex justify-end">
              <RotatingLogos stores={stores} />
            </div>
          </div>

          {/* About Severna Park - subtle banner */}
          <div className="text-center mb-8">
            <p className="text-sm text-gray-500 italic max-w-2xl mx-auto">
              Nestled between the Severn and Magothy rivers, Severna Park is a vibrant Maryland community
              with excellent schools, beautiful parks, and a thriving local business district.
            </p>
          </div>

          {/* Welcome Section with Stats */}
          <div className="card backdrop-blur-sm bg-white/95 shadow-xl border border-white/50 mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Welcome!</h2>
            <p className="text-gray-600 leading-relaxed mb-4">
              Discover The Park connects you with amazing local businesses in Severna Park, Maryland.
              Visit participating stores, scan their unique QR codes, and enter to win exciting prizes
              in our community raffles and promotions.
            </p>
            <div className="grid sm:grid-cols-3 gap-4 mt-6">
              <div className="text-center p-4 bg-primary-50 rounded-lg">
                <Store className="w-8 h-8 text-primary-600 mx-auto mb-2" />
                <p className="font-medium text-gray-900">Visit Stores</p>
                <p className="text-sm text-gray-600">Explore local businesses</p>
              </div>
              <div className="text-center p-4 bg-green-50 rounded-lg">
                <MapPin className="w-8 h-8 text-green-600 mx-auto mb-2" />
                <p className="font-medium text-gray-900">Scan QR Codes</p>
                <p className="text-sm text-gray-600">Check in at each location</p>
              </div>
              <div className="text-center p-4 bg-amber-50 rounded-lg">
                <Calendar className="w-8 h-8 text-amber-600 mx-auto mb-2" />
                <p className="font-medium text-gray-900">Win Prizes</p>
                <p className="text-sm text-gray-600">Enter community raffles</p>
              </div>
            </div>

            {/* Stats Section */}
            {stats && (stats.customers > 0 || stats.winners > 0) && (
              <div className="mt-6 pt-6 border-t border-gray-100">
                <p className="text-xs text-gray-500 uppercase tracking-wide mb-3 text-center">Community Impact</p>
                <div className="flex justify-center gap-8">
                  {stats.stores > 0 && (
                    <div className="text-center">
                      <div className="flex items-center justify-center gap-1 text-2xl font-bold text-primary-600">
                        <Store className="w-5 h-5" />
                        {stats.stores}
                      </div>
                      <p className="text-xs text-gray-500">Local Stores</p>
                    </div>
                  )}
                  {stats.customers > 0 && (
                    <div className="text-center">
                      <div className="flex items-center justify-center gap-1 text-2xl font-bold text-green-600">
                        <Users className="w-5 h-5" />
                        {stats.customers}
                      </div>
                      <p className="text-xs text-gray-500">Participants</p>
                    </div>
                  )}
                  {stats.winners > 0 && (
                    <div className="text-center">
                      <div className="flex items-center justify-center gap-1 text-2xl font-bold text-amber-600">
                        <Trophy className="w-5 h-5" />
                        {stats.winners}
                      </div>
                      <p className="text-xs text-gray-500">Winners</p>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Campaigns - Two Column Layout */}
          {loading ? (
            <div className="card backdrop-blur-sm bg-white/95 shadow-xl border border-white/50 mb-8">
              <div className="flex justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
              </div>
            </div>
          ) : (shopNowCampaigns.length > 0 || upcomingCampaigns.length > 0) ? (
            <div className="grid md:grid-cols-2 gap-6 mb-8">
              {/* Left Column - Shop Now */}
              <div>
                <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <span className="inline-block w-3 h-3 bg-green-500 rounded-full animate-pulse"></span>
                  Shop Now
                </h2>
                {shopNowCampaigns.length > 0 ? (
                  <div className="space-y-3">
                    {shopNowCampaigns.map((campaign) => (
                      <Link
                        key={campaign.id}
                        to={`/campaign/${campaign.id}`}
                        className="card backdrop-blur-sm bg-white/95 shadow-lg border border-white/50 hover:shadow-xl transition-shadow block p-4"
                      >
                        <div className="flex items-center justify-between">
                          <div className="min-w-0 flex-1">
                            <h3 className="text-lg font-bold text-gray-900 truncate">{campaign.campaignName}</h3>
                            {campaign.description && (
                              <p className="text-gray-600 text-sm mt-1 line-clamp-2">{campaign.description}</p>
                            )}
                            <div className="flex flex-wrap items-center gap-3 mt-2 text-xs text-gray-500">
                              <span className="flex items-center gap-1">
                                <Calendar className="w-3 h-3" />
                                {formatDate(campaign.startDate)} - {formatDate(campaign.endDate)}
                              </span>
                              <span className="flex items-center gap-1">
                                <Store className="w-3 h-3" />
                                {campaign.storeCount} stores
                              </span>
                            </div>
                          </div>
                          <ArrowRight className="w-5 h-5 text-gray-400 flex-shrink-0 ml-2" />
                        </div>
                      </Link>
                    ))}
                  </div>
                ) : (
                  <div className="card backdrop-blur-sm bg-white/90 shadow-md border border-white/50 p-4 text-center">
                    <p className="text-gray-500 text-sm">No active campaigns right now</p>
                  </div>
                )}
              </div>

              {/* Right Column - Coming Soon */}
              <div>
                <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <Calendar className="w-5 h-5 text-amber-500" />
                  Coming Soon
                </h2>
                {upcomingCampaigns.length > 0 ? (
                  <div className="space-y-3">
                    {upcomingCampaigns.map((campaign) => (
                      <Link
                        key={campaign.id}
                        to={`/campaign/${campaign.id}`}
                        className="card backdrop-blur-sm bg-white/90 shadow-md border border-white/50 hover:shadow-lg transition-shadow block p-4"
                      >
                        <div className="flex items-center justify-between">
                          <div className="min-w-0 flex-1">
                            <h3 className="text-lg font-bold text-gray-900 truncate">{campaign.campaignName}</h3>
                            {campaign.description && (
                              <p className="text-gray-600 text-sm mt-1 line-clamp-2">{campaign.description}</p>
                            )}
                            <div className="flex flex-wrap items-center gap-3 mt-2 text-xs text-gray-500">
                              <span className="flex items-center gap-1">
                                <Calendar className="w-3 h-3" />
                                Starts {formatDate(campaign.startDate)}
                              </span>
                              <span className="flex items-center gap-1">
                                <Store className="w-3 h-3" />
                                {campaign.storeCount} stores
                              </span>
                            </div>
                          </div>
                          <ArrowRight className="w-5 h-5 text-gray-400 flex-shrink-0 ml-2" />
                        </div>
                      </Link>
                    ))}
                  </div>
                ) : (
                  <div className="card backdrop-blur-sm bg-white/90 shadow-md border border-white/50 p-4 text-center">
                    <p className="text-gray-500 text-sm">No upcoming campaigns</p>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="card backdrop-blur-sm bg-white/95 shadow-xl border border-white/50 mb-8 text-center py-8">
              <p className="text-gray-600">No campaigns at the moment. Check back soon!</p>
            </div>
          )}

          {/* Footer */}
          <div className="text-center">
            <Link
              to="/login"
              className="text-primary-600 hover:text-primary-700 font-medium"
            >
              Store Owner or Admin? Sign in here
            </Link>
            <p className="text-xs text-gray-500 mt-4">
              Severna Park, Maryland
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
